import { menuCoachmarkStepAtom } from '@/atoms/menuCoachmarkAtoms';
import { sidebarPanelPinnedAtom } from '@/atoms/sidebarPanelAtoms';
import { useMutateMarkModalAsViewed } from '@/hooks/mutation/useMutateMarkModalAsViewed';
import { Button } from '@heidi/design-system/button';
import {
  Spotlight,
  SpotlightAnchor,
  SpotlightArrow,
  SpotlightClose,
  SpotlightContent,
  SpotlightDescription,
  SpotlightFooter,
  SpotlightHeader,
  SpotlightProgress,
  SpotlightTitle,
} from '@heidi/design-system/spotlight';
import { useTranslations } from '@heidi/runtime/i18n';
import { ModalId } from '@heidi/schema/ml-scribe';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef, type ReactNode } from 'react';

import { PANEL_EXPAND_SETTLE_MS, TOTAL_STEPS } from './constants';

interface MenuCoachmarkCreateStepProps {
  children?: ReactNode;
}

/**
 * Step 3 of the menu coachmark tour (conditional on global create flag).
 * Self-contained: reads atom state directly, provides its own handlers.
 * Shows the global Create button for starting new sessions.
 *
 * Figma: node 1519:81267
 */
export const MenuCoachmarkCreateStep = ({ children }: MenuCoachmarkCreateStepProps) => {
  const { t } = useTranslations();
  const currentStep = useAtomValue(menuCoachmarkStepAtom);
  const setStep = useSetAtom(menuCoachmarkStepAtom);
  const setIsPinned = useSetAtom(sidebarPanelPinnedAtom);
  const { mutate: markModalAsViewed } = useMutateMarkModalAsViewed();
  const transitionTimerRef = useRef<number | null>(null);

  const isOpen = currentStep === 3;

  useEffect(() => {
    return () => {
      if (transitionTimerRef.current) {
        window.clearTimeout(transitionTimerRef.current);
      }
    };
  }, []);

  const handleDismiss = useCallback(() => {
    markModalAsViewed({
      modal_id: ModalId.MENU_COACHMARK_TOUR,
      increment_count: true,
    });
  }, [markModalAsViewed]);

  const handleNext = useCallback(() => {
    setIsPinned(true);
    transitionTimerRef.current = window.setTimeout(() => {
      setStep(4);
    }, PANEL_EXPAND_SETTLE_MS);
  }, [setStep, setIsPinned]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setStep(null);
      }
    },
    [setStep]
  );

  if (!isOpen) return <>{children}</>;

  return (
    <Spotlight open={isOpen} onOpenChange={handleOpenChange}>
      {children && <SpotlightAnchor>{children}</SpotlightAnchor>}
      <SpotlightContent
        align="start"
        alignOffset={0}
        side="right"
        sideOffset={12}
        data-testid="menu-coachmark-step-3"
        onPointerDownOutside={(e) => {
          e.preventDefault();
          handleNext();
        }}
        onFocusOutside={(e) => e.preventDefault()}
      >
        <SpotlightArrow width={12} height={8} />
        <SpotlightHeader>
          <SpotlightTitle>{t('menuCoachmark.createStepTitle')}</SpotlightTitle>
          <SpotlightClose
            onClick={handleDismiss}
            data-testid="menu-coachmark-step-3-close"
          />
        </SpotlightHeader>
        <SpotlightDescription>
          {t('menuCoachmark.createStepDescription')}
        </SpotlightDescription>
        <SpotlightFooter>
          <SpotlightProgress>
            {t('menuCoachmark.stepProgress', { current: 3, total: TOTAL_STEPS })}
          </SpotlightProgress>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleNext}
            className="bg-surface text-text-primary hover:bg-background-secondary"
            data-testid="menu-coachmark-step-3-next"
          >
            {t('menuCoachmark.next')}
          </Button>
        </SpotlightFooter>
      </SpotlightContent>
    </Spotlight>
  );
};
